import { useTranslation } from 'react-i18next';
import { ORDER_ITEM_TRANSITIONS, canTransition } from '@/lib/domain';
import { OrderItemStatus } from '@/lib/shared-types';
import { Button } from '@/components/ui/button';
import { formatTime } from './shared';

/**
 * §15.2 — the whole line, not just the kitchen's half of it.
 *
 * ```text
 * PENDING → QUEUED → PREPARING → READY → AWAITING_PICKUP → PICKED_UP → SERVED
 * └──────────── the station ───────────┘└──────────── the pass ──────────────┘
 * ```
 *
 * The station steps go through `PATCH /kitchen/items/:id` (`kitchen.update`);
 * the pass steps go through `POST /kitchen/items/:id/pickup` and `/serve`,
 * which the controller gates on `orders.update` — a waiter carrying food away
 * holds no `kitchen.*` key and must still be able to close the line out.
 *
 * Which edges exist is never written down here: every control is offered only
 * when `ORDER_ITEM_TRANSITIONS` says the move is legal, so the board cannot
 * drift from the machine the service asserts against.
 */
export type ItemActionId = 'QUEUED' | 'PREPARING' | 'READY' | 'PICKED_UP' | 'SERVED';

export interface ItemAction {
  id: ItemActionId;
  lane: 'station' | 'pass';
  /** Mirrors the controller's own `@RequirePermissions()`. */
  permission: 'kitchen.update' | 'orders.update';
  /** Where the item lands once the call succeeds. */
  target: OrderItemStatus;
  /**
   * The edges that make the call legal. Pickup lists two because the service
   * walks `READY → AWAITING_PICKUP → PICKED_UP` in one request, so either the
   * first or the second edge is enough for the button to fire cleanly.
   */
  edges: OrderItemStatus[];
}

const ITEM_ACTIONS: ItemAction[] = [
  {
    id: 'QUEUED',
    lane: 'station',
    permission: 'kitchen.update',
    target: OrderItemStatus.QUEUED,
    edges: [OrderItemStatus.QUEUED],
  },
  {
    id: 'PREPARING',
    lane: 'station',
    permission: 'kitchen.update',
    target: OrderItemStatus.PREPARING,
    edges: [OrderItemStatus.PREPARING],
  },
  {
    id: 'READY',
    lane: 'station',
    permission: 'kitchen.update',
    target: OrderItemStatus.READY,
    edges: [OrderItemStatus.READY],
  },
  {
    id: 'PICKED_UP',
    lane: 'pass',
    permission: 'orders.update',
    target: OrderItemStatus.PICKED_UP,
    edges: [OrderItemStatus.AWAITING_PICKUP, OrderItemStatus.PICKED_UP],
  },
  {
    id: 'SERVED',
    lane: 'pass',
    permission: 'orders.update',
    target: OrderItemStatus.SERVED,
    edges: [OrderItemStatus.SERVED],
  },
];

/** The forward order of the line. `CANCELLED` is deliberately not on it. */
const FLOW: OrderItemStatus[] = [
  OrderItemStatus.PENDING,
  OrderItemStatus.QUEUED,
  OrderItemStatus.PREPARING,
  OrderItemStatus.READY,
  OrderItemStatus.AWAITING_PICKUP,
  OrderItemStatus.PICKED_UP,
  OrderItemStatus.SERVED,
];

const PASS_INDEX = FLOW.indexOf(OrderItemStatus.READY);

function legal(status: OrderItemStatus, action: ItemAction): boolean {
  return action.edges.some((edge) => canTransition(ORDER_ITEM_TRANSITIONS, status, edge));
}

interface ItemControl {
  action: ItemAction;
  /** `null` means it will fire; anything else is the reason it will not. */
  blocked: 'permission' | 'pickupFirst' | null;
}

/**
 * The controls this row should carry, in flow order.
 *
 * A station step appears only while it is the item's immediate next move — an
 * item on the pass has no use for a "Start" button. The two pass controls, by
 * contrast, appear together from READY onwards even when one of them cannot
 * fire yet: `AWAITING_PICKUP → SERVED` is not an edge, and an operator staring
 * at a plate that has been called away deserves to be told to collect it first
 * rather than to find the control quietly missing.
 */
function controlsFor(status: string, can: (key: string) => boolean): ItemControl[] {
  const at = FLOW.indexOf(status as OrderItemStatus);
  if (at < 0) return []; // CANCELLED — the badge is the whole story.

  return ITEM_ACTIONS.filter((action) => {
    if (FLOW.indexOf(action.target) <= at) return false; // already there or past it
    return action.lane === 'pass' ? at >= PASS_INDEX : legal(status as OrderItemStatus, action);
  }).map((action) => ({
    action,
    blocked: !can(action.permission)
      ? ('permission' as const)
      : legal(status as OrderItemStatus, action)
        ? null
        : ('pickupFirst' as const),
  }));
}

interface ItemLike {
  id: string;
  status: string;
  readyAt: string | null;
  pickedUpAt: string | null;
  servedAt: string | null;
}

interface Props {
  item: ItemLike;
  busy: boolean;
  can: (...keys: string[]) => boolean;
  onRun: (action: ItemAction) => void;
}

/**
 * The right-hand column of a board row: what can be done to this line now, and
 * — when nothing can — the reason in words. §32 measures prep time, pickup
 * delay and order-to-table off the stamps these buttons write, so the stamps
 * are shown next to the buttons that produced them.
 */
export function KitchenItemActions({ item, busy, can, onRun }: Props) {
  const { t, i18n } = useTranslation();
  const controls = controlsFor(item.status, can);

  const stamps: string[] = [];
  if (item.readyAt) {
    stamps.push(
      t('operations.kitchen.stamp.ready', { time: formatTime(item.readyAt, i18n.language) }),
    );
  }
  if (item.pickedUpAt) {
    stamps.push(
      t('operations.kitchen.stamp.pickedUp', { time: formatTime(item.pickedUpAt, i18n.language) }),
    );
  }
  if (item.servedAt) {
    stamps.push(
      t('operations.kitchen.stamp.served', { time: formatTime(item.servedAt, i18n.language) }),
    );
  }

  // One line, not one per button: two controls blocked on the same missing
  // permission is one fact about this operator, not two.
  const reasons = [...new Set(controls.map((c) => c.blocked).filter(Boolean))] as (
    'permission' | 'pickupFirst'
  )[];
  const missing = [
    ...new Set(controls.filter((c) => c.blocked === 'permission').map((c) => c.action.permission)),
  ];

  const idle = !controls.length && item.status !== OrderItemStatus.SERVED;

  return (
    <div className="flex shrink-0 flex-col items-end gap-1.5">
      {!!controls.length && (
        <div className="flex flex-wrap justify-end gap-1.5">
          {controls.map(({ action, blocked }, index) => (
            <Button
              key={action.id}
              size="sm"
              variant={index === 0 && !blocked ? 'default' : 'outline'}
              disabled={busy || !!blocked}
              title={
                blocked
                  ? t(`operations.kitchen.blocked.${blocked}`, { permission: action.permission })
                  : undefined
              }
              onClick={() => onRun(action)}
            >
              {t(`operations.kitchen.advanceTo.${action.id}`)}
            </Button>
          ))}
        </div>
      )}

      {reasons.map((reason) => (
        <p key={reason} className="max-w-[14rem] text-end text-xs text-ink-muted">
          {t(`operations.kitchen.blocked.${reason}`, { permission: missing.join(', ') })}
        </p>
      ))}

      {idle && (
        <p className="max-w-[14rem] text-end text-xs text-ink-muted">
          {t('operations.kitchen.blocked.cancelled')}
        </p>
      )}

      {!!stamps.length && (
        <p className="text-end text-xs text-ink-muted">{stamps.join(' · ')}</p>
      )}
    </div>
  );
}
